import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import { trackEdgeTypes, trackNodeTypes } from "./reactFlowTypes";
import {
  buildFlowGraphFromFormOnSubmit,
  collectGoToPageIds,
  type FlowGraphPage,
} from "./buildFlowGraph";
import type { CreateLeadNodeData, PageNodeData } from "./types";

function getPageBaseUrl(hostname: string | null): string {
  if (!hostname) return window.location.origin;
  if (hostname === "localhost") return "http://localhost:3002";
  return `https://${hostname}`;
}

export function ProductTypeTrackEditFlowTab({
  productId,
  startPageId,
  onFlowChange,
}: {
  productId: string;
  startPageId?: string;
  onFlowChange?: () => void;
}) {
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [flowStartPageId, setFlowStartPageId] = useState<string | undefined>(startPageId);
  const [nodes, setNodes] = useState<Node<PageNodeData | CreateLeadNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFlowStartPageId(startPageId);
  }, [startPageId]);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const baseUrl = useMemo(
    () => getPageBaseUrl(customDomainHostname),
    [customDomainHostname],
  );
  const defaultViewport = useMemo(() => ({ x: 40, y: 40, zoom: 1 }), []);

  const loadFlowGraph = useCallback(async () => {
    if (!flowStartPageId) {
      setNodes([]);
      setEdges([]);
      return;
    }

    setIsLoadingGraph(true);
    setError(null);

    try {
      const startRes = await fetch(`/api/pages/${flowStartPageId}`);
      if (!startRes.ok) {
        throw new Error("Failed to load flow start page");
      }

      const startData = (await startRes.json()) as { page?: FlowGraphPage };
      const startPage = startData.page;
      if (!startPage) {
        throw new Error("Flow start page not found");
      }

      const goToPageIds = collectGoToPageIds(startPage);
      const pagesById: Record<string, FlowGraphPage> = {
        [startPage._id]: startPage,
      };

      await Promise.all(
        goToPageIds.map(async (pageId) => {
          if (pagesById[pageId]) return;
          const res = await fetch(`/api/pages/${pageId}`);
          if (!res.ok) return;
          const data = (await res.json()) as { page?: FlowGraphPage };
          if (data.page) {
            pagesById[pageId] = data.page;
          }
        }),
      );

      const graph = buildFlowGraphFromFormOnSubmit({
        startPage,
        pagesById,
        baseUrl,
      });
      setNodes(graph.nodes);
      setEdges(graph.edges);
    } catch (e) {
      console.error("Load flow graph error:", e);
      setError(e instanceof Error ? e.message : "Failed to load flow");
      setNodes([]);
      setEdges([]);
    } finally {
      setIsLoadingGraph(false);
    }
  }, [baseUrl, flowStartPageId]);

  useEffect(() => {
    void loadFlowGraph();
  }, [loadFlowGraph]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const res = await fetch(`/api/products/${productId}/generate-flow`, {
        method: "POST",
      });
      const data = (await res.json()) as {
        error?: string;
        product?: { flow?: { startPageId?: string } };
      };

      if (!res.ok) {
        setError(data.error || "Failed to generate flow");
        return;
      }

      const nextStartPageId = data.product?.flow?.startPageId;
      setFlowStartPageId(nextStartPageId);
      onFlowChange?.();
    } catch (e) {
      console.error("Generate flow error:", e);
      setError("Failed to generate flow");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const res = await fetch(`/api/products/${productId}/regenerate-flow`, {
        method: "POST",
      });
      const data = (await res.json()) as {
        error?: string;
        product?: { flow?: { startPageId?: string } };
      };

      if (!res.ok) {
        setError(data.error || "Failed to regenerate flow");
        return;
      }

      const nextStartPageId = data.product?.flow?.startPageId;
      setFlowStartPageId(nextStartPageId);
      onFlowChange?.();
    } catch (e) {
      console.error("Regenerate flow error:", e);
      setError("Failed to regenerate flow");
    } finally {
      setIsGenerating(false);
    }
  };

  if (!flowStartPageId) {
    return (
      <div className="h-[480px] rounded-lg border border-gray-200 flex flex-col items-center justify-center gap-3 px-4">
        {error ? (
          <p className="text-sm text-destructive text-center">{error}</p>
        ) : null}
        <Button type="button" onClick={handleGenerate} disabled={isGenerating}>
          {isGenerating ? "Generating..." : "Generate"}
        </Button>
      </div>
    );
  }

  if (isLoadingGraph) {
    return (
      <div className="h-[480px] rounded-lg border border-gray-200 flex items-center justify-center text-sm text-muted-foreground">
        Loading flow...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[480px] rounded-lg border border-gray-200 flex flex-col items-center justify-center gap-3 px-4">
        <p className="text-sm text-destructive text-center">{error}</p>
        <Button type="button" variant="outline" onClick={() => void loadFlowGraph()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="h-[480px] rounded-lg border border-gray-200 overflow-hidden relative">
      <div className="absolute top-2 right-2 z-10">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRegenerate}
          disabled={isGenerating}
        >
          {isGenerating ? "Regenerating..." : "Regenerate"}
        </Button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={trackNodeTypes}
        edgeTypes={trackEdgeTypes}
        defaultViewport={defaultViewport}
        nodesConnectable={false}
        nodesDraggable={false}
        elementsSelectable={false}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        zoomOnScroll
        panOnScroll
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}
